'use client';

import { createBrowserClient } from '@supabase/ssr';
import type { Session, SupabaseClient } from '@supabase/supabase-js';

import { Env } from '@/lib/env';

type SessionAccessToken = Pick<Session, 'access_token'>;

function localRealtimeOptions() {
  if (typeof window === 'undefined') {
    return {};
  }
  const supabaseUrl = new URL(Env.NEXT_PUBLIC_SUPABASE_URL!);
  if (supabaseUrl.protocol !== 'http:' || supabaseUrl.hostname !== 'localhost') {
    return {};
  }
  return {
    realtime: {
      // Cookies are shared across localhost ports. Sending the app's cookies to
      // local Realtime can exceed its header limit and reject the handshake (431).
      // Change only the websocket host: changing the Supabase base URL would also
      // change its auth storage key and lose the existing signed-in session.
      transport: class extends WebSocket {
        constructor(url: string | URL, protocols?: string | string[]) {
          const endpoint = new URL(url);
          if (endpoint.protocol === 'ws:' && endpoint.hostname === 'localhost') {
            endpoint.hostname = '127.0.0.1';
          }
          super(endpoint.toString(), protocols);
        }
      },
    },
  };
}

export function createClient() {
  return createBrowserClient(
    Env.NEXT_PUBLIC_SUPABASE_URL!,
    Env.NEXT_PUBLIC_SUPABASE_PUBLISHABLE_KEY!,
    localRealtimeOptions(),
  );
}

/**
 * Non-singleton client with its own realtime websocket. Latency-sensitive
 * subscriptions use this so token churn from sibling channels on the shared
 * socket cannot stall their broadcasts.
 */
export function createDedicatedClient() {
  return createBrowserClient(
    Env.NEXT_PUBLIC_SUPABASE_URL!,
    Env.NEXT_PUBLIC_SUPABASE_PUBLISHABLE_KEY!,
    { ...localRealtimeOptions(), isSingleton: false },
  );
}

async function resolveRealtimeAuthToken(
  client: SupabaseClient,
  session?: SessionAccessToken | null,
) {
  if (session?.access_token) {
    return session.access_token;
  }

  const {
    data: { session: currentSession },
    error,
  } = await client.auth.getSession();
  if (error) {
    throw error;
  }

  return currentSession?.access_token ?? null;
}

/**
 * Puts the signed-in user's access token on the realtime client so private channels join as that
 * user. Resolves to `null` and leaves realtime auth untouched when there is no session, because
 * private channels deny the publishable key. Rejects when the session cannot be loaded.
 */
export async function syncRealtimeAuth(
  session?: SessionAccessToken | null,
  options?: { dedicated?: boolean },
): Promise<SupabaseClient | null> {
  const client = options?.dedicated ? createDedicatedClient() : createClient();
  let kept = false;
  try {
    const nextToken = await resolveRealtimeAuthToken(client, session);
    if (!nextToken) {
      return null;
    }
    await client.realtime.setAuth(nextToken);
    kept = true;
    return client;
  } finally {
    if (options?.dedicated && !kept) {
      // Nothing keeps a dedicated client this does not return, so its auth must not keep refreshing.
      void client.auth.stopAutoRefresh();
    }
  }
}
